import { useState, type ComponentType, type SVGProps } from "react"

import { cn } from "@/lib/utils"
import { AppRoutes } from "@/shared/lib/routes"
import UserIcon from "@/assets/img/profile/menu/user.svg?react"
import SupportIcon from "@/assets/img/profile/menu/support.svg?react"
import TermsIcon from "@/assets/img/profile/menu/terms.svg?react"
import PrivacyIcon from "@/assets/img/profile/menu/privacy.svg?react"
import CookieIcon from "@/assets/img/profile/menu/cookie.svg?react"
import MyPlanIcon from "@/assets/img/profile/menu/my_plan.svg?react"
import RefundIcon from "@/assets/img/profile/menu/refund.svg?react"
import LogoutIcon from "@/assets/img/profile/menu/logout.svg?react"
import ChevronRight from "@/assets/img/profile/menu/chevron_right.svg?react"
import FindAdvisor from "@/assets/img/profile/find_my_advisor.webp"

import { LogoutModal } from "./logout-modal"

const SUPPORT_EMAIL = "support@astrology.clinic"

type MenuIcon = ComponentType<SVGProps<SVGSVGElement>>

interface MenuItem {
  id: string
  label: string
  Icon: MenuIcon
  onClick?: () => void
  danger?: boolean
  chevron?: boolean
  disabled?: boolean
}

interface HelpAndLegalProps {
  onEditProfile: () => void
  onFindAdvisor: () => void
  onMyPlan: () => void
}

/** Help & Legal menu (profile, support, legal, plan, refund, logout). Ported from ProfileV4. */
export function HelpAndLegal({ onEditProfile, onFindAdvisor, onMyPlan }: HelpAndLegalProps) {
  const [logoutOpen, setLogoutOpen] = useState(false)

  const items: MenuItem[] = [
    { id: "profile", label: "My profile", Icon: UserIcon, onClick: onEditProfile },
    {
      id: "support",
      label: "Support",
      Icon: SupportIcon,
      onClick: () => window.open(`mailto:${SUPPORT_EMAIL}`, "_blank"),
    },
    {
      id: "terms",
      label: "Terms of use",
      Icon: TermsIcon,
      onClick: () => window.open(AppRoutes.TERMS, "_blank"),
    },
    {
      id: "privacy",
      label: "Privacy Policy",
      Icon: PrivacyIcon,
      onClick: () => window.open(AppRoutes.PRIVACY, "_blank"),
    },
    {
      id: "cookie",
      label: "Cookie Policy",
      Icon: CookieIcon,
      onClick: () => window.open(AppRoutes.COOKIES, "_blank"),
    },
    {
      id: "plan",
      label: "My Plan",
      Icon: MyPlanIcon,
      onClick: onMyPlan,
    },
    {
      id: "refund",
      label: "Ask for refund",
      Icon: RefundIcon,
      onClick: () => window.open(`mailto:${SUPPORT_EMAIL}?subject=Refund%20request`, "_blank"),
    },
    {
      id: "logout",
      label: "Log out",
      Icon: LogoutIcon,
      onClick: () => setLogoutOpen(true),
      danger: true,
      chevron: false,
    },
  ]

  return (
    <div className="flex flex-col gap-4 p-4 py-10">
      <h3 className="font-display text-2xl font-medium text-purple-light">Help &amp; Legal</h3>

      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onFindAdvisor}
          className="flex items-center gap-4 overflow-hidden rounded-xl bg-dark-surface pl-4 text-left"
        >
          <span className="flex min-w-0 flex-1 flex-col gap-2 py-4">
            <span className="flex flex-col gap-1">
              <span className="font-body text-base font-semibold text-purple-light">
                Find my advisor
              </span>
              <span className="font-body text-sm text-purple-light/70">
                Take a short quiz - and get matched with the perfect advisor
              </span>
            </span>
            <span className="w-fit rounded-[15px] bg-gold/10 px-3 py-2 font-body text-[10px] font-medium uppercase text-gold">
              Personalized for you
            </span>
          </span>
          <img src={FindAdvisor} alt="" className="h-[133px] w-[120px] shrink-0 object-cover" />
        </button>

        {items.map(({ id, label, Icon, onClick, danger, chevron = true, disabled }) => (
          <button
            key={id}
            type="button"
            onClick={onClick}
            disabled={disabled || !onClick}
            className={cn(
              "flex items-center gap-2 rounded-xl py-4 pl-4 text-left disabled:opacity-40",
              danger ? "bg-peach/10 pr-4" : "bg-dark-surface",
              chevron ? "pr-2" : "pr-4",
            )}
          >
            <span
              className={cn(
                "flex size-6 shrink-0 items-center justify-center [&_svg]:size-5",
                danger ? "text-peach" : "text-purple-light",
              )}
            >
              <Icon />
            </span>
            <span
              className={cn(
                "min-w-0 flex-1 font-body text-base font-semibold",
                danger ? "text-peach" : "text-purple-light",
              )}
            >
              {label}
            </span>
            {chevron && (
              <span className="flex size-6 shrink-0 items-center justify-center text-purple-light [&_svg]:size-6">
                <ChevronRight />
              </span>
            )}
          </button>
        ))}
      </div>

      {logoutOpen && <LogoutModal onClose={() => setLogoutOpen(false)} />}
    </div>
  )
}
